import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Search, Plus, Edit, Trash2, Phone, Mail, Building2, Tag, MessageSquare, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";
import { z } from "zod";
import { useSMSEnabled } from "@/hooks/useSMSEnabled";
import { LogActivityDialog } from "./LogActivityDialog";

type Contact = Tables<"crm_contacts">;

const contactSchema = z.object({
  name: z.string().trim().max(100, "Name must be less than 100 characters").optional(),
  email: z.string().trim().email("Invalid email address").max(255).optional().or(z.literal('')),
  company: z.string().trim().max(100).optional(),
  phone_number: z.string()
    .trim()
    .regex(/^\+[1-9]\d{1,14}$/, "Phone number must be in E.164 format (e.g., +1234567890)"),
  notes: z.string().max(1000, "Notes must be less than 1000 characters").optional(),
});

export const ContactManagement = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [logActivityOpen, setLogActivityOpen] = useState(false);
  const [selectedContactForActivity, setSelectedContactForActivity] = useState<Contact | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { data: smsEnabled, isLoading: smsLoading } = useSMSEnabled();

  const [formData, setFormData] = useState<{
    phone_number: string;
    name: string;
    email: string;
    company: string;
    lifecycle_stage: "lead" | "prospect" | "customer" | "inactive";
    priority: "low" | "medium" | "high";
    notes: string;
  }>({
    phone_number: "",
    name: "",
    email: "",
    company: "",
    lifecycle_stage: "lead",
    priority: "medium",
    notes: "",
  });

  useEffect(() => {
    fetchContacts();

    const channel = supabase
      .channel('crm-contacts-channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_contacts'
        },
        () => {
          fetchContacts();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    let filtered = contacts;
    
    if (searchQuery) {
      filtered = filtered.filter(contact =>
        contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.phone_number.includes(searchQuery) ||
        contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.company?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (stageFilter !== "all") {
      filtered = filtered.filter(contact => contact.lifecycle_stage === stageFilter);
    }

    setFilteredContacts(filtered);
  }, [contacts, searchQuery, stageFilter]);

  const fetchContacts = async () => {
    const { data, error } = await supabase
      .from('crm_contacts')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching contacts:', error);
      toast({
        title: "Error",
        description: "Failed to load contacts",
        variant: "destructive",
      });
    } else {
      setContacts(data || []);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      contactSchema.parse(formData);

      if (editingContact) {
        const { error } = await supabase
          .from('crm_contacts')
          .update(formData)
          .eq('id', editingContact.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Contact updated successfully",
        });
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        
        const { error } = await supabase
          .from('crm_contacts')
          .insert({
            ...formData,
            created_by: user?.id
          });

        if (error) throw error;

        toast({
          title: "Success",
          description: "Contact created successfully",
        });
      }

      setDialogOpen(false);
      resetForm();
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to save contact",
          variant: "destructive",
        });
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this contact?')) return;

    const { error } = await supabase
      .from('crm_contacts')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to delete contact",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Contact deleted successfully",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      phone_number: "",
      name: "",
      email: "",
      company: "",
      lifecycle_stage: "lead",
      priority: "medium",
      notes: "",
    });
    setEditingContact(null);
  };

  const openEditDialog = (contact: Contact) => {
    setEditingContact(contact);
    setFormData({
      phone_number: contact.phone_number,
      name: contact.name || "",
      email: contact.email || "",
      company: contact.company || "",
      lifecycle_stage: (contact.lifecycle_stage || "lead") as "lead" | "prospect" | "customer" | "inactive",
      priority: (contact.priority || "medium") as "low" | "medium" | "high",
      notes: contact.notes || "",
    });
    setDialogOpen(true);
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'lead': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'prospect': return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
      case 'customer': return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'inactive': return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
      default: return '';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500/10 text-red-600 border-red-500/20';
      case 'medium': return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
      case 'low': return 'bg-green-500/10 text-green-600 border-green-500/20';
      default: return '';
    }
  };

  const handleStartConversation = async (contactId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('start-conversation', {
        body: { crm_contact_id: contactId }
      });

      if (error) throw error;

      // Navigate to Dashboard with conversation selected
      navigate(`/dashboard?conversation=${data.conversation_id}`);
      
      toast({
        title: "Conversation started",
        description: "Ready to send a message",
      });
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast({
        title: "Error",
        description: "Failed to start conversation",
        variant: "destructive",
      });
    }
  };

  const handleEmailClick = (contact: Contact) => {
    if (contact.email) {
      window.location.href = `mailto:${contact.email}`;
      // Optionally log the activity
      setTimeout(() => {
        setSelectedContactForActivity(contact);
        setLogActivityOpen(true);
      }, 100);
    }
  };

  const handlePhoneClick = (contact: Contact) => {
    if (contact.phone_number) {
      window.location.href = `tel:${contact.phone_number}`;
      // Optionally log the activity
      setTimeout(() => {
        setSelectedContactForActivity(contact);
        setLogActivityOpen(true);
      }, 100);
    }
  };

  const handleLogActivity = (contact: Contact) => {
    setSelectedContactForActivity(contact);
    setLogActivityOpen(true);
  };

  if (loading) {
    return <div>Loading contacts...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Contact Management</CardTitle>
            <CardDescription>Manage your customer relationships</CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingContact ? 'Edit Contact' : 'New Contact'}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone Number *</Label>
                    <Input
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      placeholder="+1234567890"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Input
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Acme Inc."
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Lifecycle Stage</Label>
                    <Select
                      value={formData.lifecycle_stage}
                      onValueChange={(value: any) => setFormData({ ...formData, lifecycle_stage: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lead">Lead</SelectItem>
                        <SelectItem value="prospect">Prospect</SelectItem>
                        <SelectItem value="customer">Customer</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value: any) => setFormData({ ...formData, priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Add any relevant notes..."
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  {editingContact ? 'Update' : 'Create'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by stage" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              <SelectItem value="lead">Leads</SelectItem>
              <SelectItem value="prospect">Prospects</SelectItem>
              <SelectItem value="customer">Customers</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contact</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredContacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No contacts found
                </TableCell>
              </TableRow>
            ) : (
              filteredContacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">{contact.name || 'Unknown'}</div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="h-3 w-3" />
                        {contact.phone_number}
                      </div>
                      {contact.email && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          {contact.email}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {contact.company && (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        {contact.company}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn("text-xs", getStageColor(contact.lifecycle_stage || 'lead'))}>
                      {contact.lifecycle_stage}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn("text-xs", getPriorityColor(contact.priority || 'medium'))}>
                      {contact.priority}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {contact.email && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEmailClick(contact)}
                          title="Send email"
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                      )}
                      {contact.phone_number && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePhoneClick(contact)}
                          title="Call"
                        >
                          <Phone className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleLogActivity(contact)}
                        title="Log activity"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      {smsEnabled && !smsLoading && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStartConversation(contact.id)}
                          title="Send SMS"
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(contact)}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(contact.id)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>

      <LogActivityDialog
        open={logActivityOpen}
        onOpenChange={setLogActivityOpen}
        contactId={selectedContactForActivity?.id || ''}
        contactName={selectedContactForActivity?.name || ''}
      />
    </Card>
  );
};
